import { useState, useEffect, useContext } from "react";
import { doc, getDoc, updateDoc, collection, addDoc } from "firebase/firestore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import jsPDF from "jspdf";
import { formatDate } from "../utils/formatDate";
import { encryptText, decryptText, getEncryptionSecret } from "../utils";
import { EncryptionContext } from "./EncryptionContext.jsx";

function normalizeDays(dayValue) {
  if (!dayValue) return [];
  if (Array.isArray(dayValue)) return dayValue;
  if (typeof dayValue === "string") {
    return dayValue
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }
  return [];
}

function formatDaysLabel(days) {
  const normalized = normalizeDays(days);
  return normalized.length ? normalized.join(", ") : "";
}

const getTodayDate = () => new Date().toISOString().split("T")[0];

const journalEditorModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const journalEditorFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function containsHtml(value = "") {
  return /<[^>]+>/.test(value);
}

function getPromptPlainText(value = "") {
  const element = document.createElement("div");
  element.innerHTML = value;
  return element.textContent || element.innerText || "";
}

function buildPromptEntryHtml(prompts = []) {
  return prompts
    .map((prompt, index) => {
      const formattedText = containsHtml(prompt.text)
        ? prompt.text
        : `<p>${escapeHtml(prompt.text)}</p>`;

      return `<div class="journal-prompt"><p><strong>${index + 1}.</strong></p>${formattedText}</div><p><br></p>`;
    })
    .join("");
}

function JournalTab({ user, db, selectedCycle, setDecryptionError, prompts, tasks, setTasks, selectedDate, loadingTasks }) {
  const { passphrase } = useContext(EncryptionContext);
  const [entry, setEntry] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Get today's day in the device's timezone
  const today = new Date();
  const todayDayOfWeek = today.toLocaleString("en-US", { weekday: "long" }); // "Wednesday" on August 27, 2025

  // Log tasks to debug
  console.log("All tasks:", tasks);

  // Filter tasks into "Today's Tasks" and "General Tasks"
  const todaysTasks = tasks.filter((task) =>
    normalizeDays(task.dayOfWeek).some((day) => day.toLowerCase() === todayDayOfWeek.toLowerCase())
  );
  const generalTasks = tasks.filter((task) => normalizeDays(task.dayOfWeek).length === 0);

  // Sort tasks by priority (High, Medium, Low)
  const priorityOrder = { High: 1, Medium: 2, Low: 3 };
  const sortedTodaysTasks = [...todaysTasks].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  const sortedGeneralTasks = [...generalTasks].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  useEffect(() => {
    const fetchEntry = async () => {
      if (!user || !selectedCycle || !selectedDate) {
        console.log("fetchEntry early exit: user:", !!user, "selectedCycle:", !!selectedCycle, "selectedDate:", selectedDate);
        setEntry("");
        return;
      }
      try {
        console.log("Fetching entry for", selectedDate, "prompts:", prompts || "No prompts provided");
        // Check journalEntries collection (previous version's path)
        const entryRef = doc(db, `users/${user.uid}/cycles/${selectedCycle.id}/journalEntries`, selectedDate);
        const entrySnap = await getDoc(entryRef);
        if (entrySnap.exists()) {
          const data = entrySnap.data();
          console.log("Fetched entry data:", data);
          const decryptedText = await decryptText(data.text, user, getEncryptionSecret(user, passphrase), setDecryptionError);
          // Load prompts if entry is empty or only whitespace
          if (!decryptedText || decryptedText.replace(/<[^>]+>/g, "").trim() === "") {
            console.log("Empty or whitespace entry, loading prompts");
            const promptText = (prompts && prompts.length > 0)
              ? buildPromptEntryHtml(prompts)
              : "";
            setEntry(promptText);
          } else {
            setEntry(decryptedText);
          }
        } else {
          console.log("No entry found, loading prompts");
          const promptText = (prompts && prompts.length > 0)
            ? buildPromptEntryHtml(prompts)
            : "";
          setEntry(promptText);
        }
        setDecryptionError("");
      } catch (error) {
        console.error("Error fetching entry:", error, "code:", error.code);
        setDecryptionError("Failed to load journal entry. Please try again.");
      }
    };
    fetchEntry();
  }, [user, db, selectedCycle, selectedDate, setDecryptionError, prompts, passphrase]);

  const saveEntry = async () => {
    if (!user || !selectedCycle || !selectedDate) {
      console.log("saveEntry aborted: user:", !!user, "selectedCycle:", !!selectedCycle, "selectedDate:", selectedDate);
      setDecryptionError("Cannot save: Missing user, cycle, or date.");
      return;
    }
    // Check if entry is empty (after stripping HTML tags)
    const plainText = entry.replace(/<[^>]+>/g, "").trim();
    if (!plainText) {
      console.log("saveEntry aborted: Entry is empty");
      setDecryptionError("Cannot save empty journal entry.");
      return;
    }
    setSaving(true);
    try {
      const encryptionSecret = getEncryptionSecret(user, passphrase);
      const encryptedText = await encryptText(entry, user, encryptionSecret);
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      console.log("Attempting to save entry to:", `${cyclePath}/journalEntries`);
      const data = {
        text: encryptedText,
        date: selectedDate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await addDoc(collection(db, `${cyclePath}/journalEntries`), data);
      console.log("Entry saved successfully for", selectedDate);
      setLastSaved(new Date());
      setDecryptionError("");
    } catch (error) {
      console.error("Error saving entry:", error, "code:", error.code);
      setDecryptionError(`Failed to save journal entry: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEntryChange = (value) => {
    setEntry(value); // Update state without saving
  };

  const toggleTaskComplete = async (id) => {
    if (!selectedCycle) {
      console.log("toggleTaskComplete aborted: No selected cycle");
      return;
    }
    setSaving(true);
    const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
    const taskRef = doc(db, `${cyclePath}/schedules/shared/tasks`, id);
    const task = tasks.find((t) => t.id === id);
    try {
      const completed = !task.completed;
      const lastCompletedDate = completed ? getTodayDate() : null;
      await updateDoc(taskRef, { completed, lastCompletedDate, updatedAt: new Date() });
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === id ? { ...t, completed, lastCompletedDate, updatedAt: new Date() } : t
        )
      );
      window.dispatchEvent(new CustomEvent("updateTasks", {
        detail: { taskId: id, completed, lastCompletedDate },
      }));
      setDecryptionError("");
    } catch (error) {
      console.error("Error updating task status:", error, "code:", error.code);
      setDecryptionError("Error updating task status. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let yOffset = 10;

    doc.setFontSize(16);
    doc.text(`Journal for ${selectedCycle.name} - ${formatDate(selectedDate)}`, 10, yOffset);
    yOffset += 15;

    doc.setFontSize(12);
    doc.text("Prompts:", 10, yOffset);
    yOffset += 10;
    if (!prompts || prompts.length === 0) {
      doc.text("No prompts available for this cycle.", 10, yOffset);
      yOffset += 10;
    } else {
      prompts.forEach((prompt, index) => {
        const lines = doc.splitTextToSize(`${index + 1}. ${containsHtml(prompt.text) ? getPromptPlainText(prompt.text) : prompt.text}`, 180);
        doc.text(lines, 10, yOffset);
        yOffset += lines.length * 10 + 5;
        if (yOffset > 270) {
          doc.addPage();
          yOffset = 10;
        }
      });
    }

    yOffset += 10;

    doc.text("Today's Tasks:", 10, yOffset);
    yOffset += 10;
    if (sortedTodaysTasks.length === 0) {
      doc.text("No tasks scheduled for today.", 10, yOffset);
      yOffset += 10;
    } else {
      sortedTodaysTasks.forEach((task, index) => {
        const dayLabel = formatDaysLabel(task.dayOfWeek);
        const taskText = `${index + 1}. ${task.text}${task.time ? ` (${task.time})` : ""} [${task.priority}]${dayLabel ? ` - ${dayLabel}` : ""}`;
        const lines = doc.splitTextToSize(taskText, 180);
        doc.text(lines, 10, yOffset);
        yOffset += lines.length * 10 + 5;
        if (yOffset > 270) {
          doc.addPage();
          yOffset = 10;
        }
      });
    }

    yOffset += 10;

    doc.text("General Tasks:", 10, yOffset);
    yOffset += 10;
    if (sortedGeneralTasks.length === 0) {
      doc.text("No general tasks available.", 10, yOffset);
      yOffset += 10;
    } else {
      sortedGeneralTasks.forEach((task, index) => {
        const dayLabel = formatDaysLabel(task.dayOfWeek);
        const taskText = `${index + 1}. ${task.text}${task.time ? ` (${task.time})` : ""} [${task.priority}]${dayLabel ? ` - ${dayLabel}` : ""}`;
        const lines = doc.splitTextToSize(taskText, 180);
        doc.text(lines, 10, yOffset);
        yOffset += lines.length * 10 + 5;
        if (yOffset > 270) {
          doc.addPage();
          yOffset = 10;
        }
      });
    }

    yOffset += 10;
    doc.text("Journal Entry:", 10, yOffset);
    yOffset += 10;
    const entryText = entry.replace(/<[^>]+>/g, "");
    const entryLines = doc.splitTextToSize(entryText || "No entry provided.", 180);
    doc.text(entryLines, 10, yOffset);
    yOffset += entryLines.length * 10 + 5;

    doc.save(`journal-${selectedCycle.name}-${formatDate(selectedDate)}.pdf`);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "text-red-600 dark:text-red-400";
      case "Medium":
        return "text-orange-600 dark:text-orange-400";
      case "Low":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-text-light dark:text-text-dark";
    }
  };

  return (
    <div className="card animate-fade-in w-full mt-0">
      <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">
        Journal Entry for {formatDate(selectedDate)}
      </h2>
      {/* Journal Entry Field (ReactQuill) */}
      <div className="mb-6">
        <ReactQuill
          value={entry}
          onChange={handleEntryChange}
          className="journal-rich-editor bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark rounded"
          placeholder="Write your journal entry here..."
          modules={journalEditorModules}
          formats={journalEditorFormats}
          readOnly={saving}
        />
        <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
          <button
            onClick={saveEntry}
            disabled={saving}
            className={`btn-primary w-full md:w-auto ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label="Add journal entry"
          >
          {saving ? "Saving..." : "Add Entry"}
          </button>
          <button
            onClick={exportToPDF}
            disabled={saving}
            className={`btn-primary w-full md:w-auto ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label="Export journal to PDF"
          >
            Export to PDF
          </button>
          {lastSaved && (
            <p className="text-neutral">
              Last saved: {formatDate(lastSaved.toISOString(), true)}
            </p>
          )}
        </div>
      </div>
      {/* Today's Tasks Section */}
      <div className="mt-6">
        <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">Today's Tasks</h3>
        {loadingTasks ? (
          <p className="text-neutral">Loading tasks...</p>
        ) : sortedTodaysTasks.length > 0 ? (
          <ul className="space-y-2">
            {sortedTodaysTasks.map((task) => (
              <li key={task.id} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => toggleTaskComplete(task.id)}
                  className="h-5 w-5 text-accent rounded focus:ring-accent"
                  disabled={saving}
                  aria-label={`Toggle task ${task.text} completion`}
                />
                <span className={`${task.completed ? "line-through text-neutral" : getPriorityColor(task.priority)}`}>
                  {task.text} {task.time && `(${task.time})`} {task.priority && `[${task.priority}]`} {formatDaysLabel(task.dayOfWeek) && `(${formatDaysLabel(task.dayOfWeek)})`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-neutral">No tasks scheduled for {todayDayOfWeek}.</p>
        )}
      </div>
      {/* General Tasks Section */}
      <div className="mt-6">
        <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">General Tasks</h3>
        {loadingTasks ? (
          <p className="text-neutral">Loading tasks...</p>
        ) : sortedGeneralTasks.length > 0 ? (
          <ul className="space-y-2">
            {sortedGeneralTasks.map((task) => (
              <li key={task.id} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => toggleTaskComplete(task.id)}
                  className="h-5 w-5 text-accent rounded focus:ring-accent"
                  disabled={saving}
                  aria-label={`Toggle task ${task.text} completion`}
                />
                <span className={`${task.completed ? "line-through text-neutral" : getPriorityColor(task.priority)}`}>
                  {task.text} {task.time && `(${task.time})`} {task.priority && `[${task.priority}]`} {formatDaysLabel(task.dayOfWeek) && `(${formatDaysLabel(task.dayOfWeek)})`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-neutral">No general tasks available.</p>
        )}
      </div>
    </div>
  );
}

export default JournalTab;